import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { ChevronDown, Eye, MoreHorizontal, Trash2 } from 'lucide-react';

import { DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { useDebounce } from '@/hooks';
import { Asset, AssetRow, AssetStatus, AssetType, DateRange, RwaStatus } from '@/types';
import { useNavigate } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFractionalizeAssetMutation, useGetAllAssetsQuery } from '@/store/api/assetsApi';
import { Label } from '@/components/ui/label';
import { FractionalizeAssetModal } from './FractionalizeAssetModal';
import { DateRangePicker } from '@/components/common/DateRangePicker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function UserActions({ asset }: { asset: Asset }) {
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => navigate(`/edit-asset/${asset.id}`)}
          className="text-destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Edit Asset
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const mapAssetToRow = (asset: Asset): AssetRow => {
  return {
    id: asset.id,

    assetName: asset.assetTitle ?? '—',
    ownerName: asset.currentOwnerName ?? '—',
    assetType: asset.assetType ?? '—',

    ownerKycStatus: asset.isVerified ? 'Completed' : 'Pending',
    rwaStatus: asset.rwaRequest?.status,
    status: asset.status,
    createdAt: asset.createdAt ? new Date(asset.createdAt).toLocaleDateString() : '—',
    totalFractions: asset.totalFractions ?? 0,
    soldFractions: asset.soldFractions ?? 0,
    remainingFractions: asset.remainingFractions ?? 0,
    price: asset?.price,
    rwaRequest: asset?.rwaRequest,
  };
};

export function AssetList() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const navigate = useNavigate();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [fractionModalOpen, setFractionModalOpen] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<any>(null);
  const [rwaStatus, setRwaStatus] = useState<RwaStatus>(RwaStatus.Approved);
  const [assetStatus, setAssetStatus] = useState<AssetStatus>(AssetStatus.Draft);
  const [assetType, setAssetType] = useState<AssetType>(AssetType.All);
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });
  const { data, isLoading, isError } = useGetAllAssetsQuery({
    skip: pageIndex * pageSize,
    take: pageSize,
    search: debouncedSearch || undefined,
    rwaStatus: rwaStatus,
    assetStatus: assetStatus,
    fromDate: dateRange.from?.toISOString(),
    toDate: dateRange.to?.toISOString(),
    ...(assetType !== 'All' && { assetType: assetType }),
  });

  const assets: AssetRow[] = data?.data?.map(mapAssetToRow) ?? [];
  const totalCount = data?.count ?? 0;

  const [fractionalizeAsset, { isLoading: isFractionalizing }] = useFractionalizeAssetMutation();

  const availableAssets: ColumnDef<AssetRow>[] = [
    {
      header: 'Initial Price',
      cell: ({ row }) => {
        const price = row.original.price;

        if (!price) {
          return <span className="text-gray-400">—</span>;
        }

        return <span>₹{price.initialPricePerFraction}</span>;
      },
    },

    {
      header: 'Current Price',
      cell: ({ row }) => {
        const price = row.original.price;

        if (!price) {
          return <span className="text-gray-400">—</span>;
        }

        return <span>₹{price.currentPricePerFraction}</span>;
      },
    },
    {
      header: 'Total Fractions',
      accessorKey: 'totalFractions',
    },
    {
      header: 'Sold Fractions',
      accessorKey: 'soldFractions',
    },
    {
      header: 'Remaining Fractions',
      accessorKey: 'remainingFractions',
    },
  ];

  const draftAssets: ColumnDef<AssetRow> = {
    header: 'Fractionalize',
    cell: ({ row }) => {
      const { rwaStatus, status, id } = row.original;

      const canFractionalize = rwaStatus === RwaStatus.Approved && status === AssetStatus.Draft;

      if (!canFractionalize) {
        return <span className="text-gray-400">—</span>;
      }

      return (
        <Button
          size="sm"
          disabled={isFractionalizing}
          onClick={() => {
            setSelectedAssetId(id);
            setFractionModalOpen(true);
          }}
        >
          Fractionalize
        </Button>
      );
    },
  };

  const columns: ColumnDef<AssetRow>[] = [
    {
      header: 'Asset Name',
      accessorKey: 'assetName',
      cell: ({ row }) => (
        <span className="block max-w-[140px] truncate">{row.original.assetName}</span>
      ),
    },
    {
      header: 'Owner Name',
      accessorKey: 'ownerName',
    },
    {
      header: 'Asset Type',
      accessorKey: 'assetType',
    },
    {
      header: 'RWA Status',
      cell: ({ row }) => {
        const status = row.original.rwaStatus;

        const statusStyles: Record<string, string> = {
          Submitted: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          Approved: 'bg-green-100 text-green-800 border-green-200',
          Rejected: 'bg-red-100 text-red-800 border-red-200',
        };

        return (
          <span
            className={`inline-flex items-center capitalize rounded-full border px-3 py-1 text-xs font-medium ${
              statusStyles[status]
            }`}
          >
            {status.toLowerCase()}
          </span>
        );
      },
    },

    {
      header: 'Asset Status',
      cell: ({ row }) => {
        const status = row.original.status as AssetStatus;
        const assetStatusStyles: Record<AssetStatus, string> = {
          [AssetStatus.Draft]: 'bg-gray-100 text-gray-800 border-gray-200',
          [AssetStatus.Available]: 'bg-blue-100 text-blue-800 border-blue-200',
          [AssetStatus.Funded]: 'bg-green-100 text-green-800 border-green-200',
          [AssetStatus.Exited]: 'bg-purple-100 text-purple-800 border-purple-200',
        };
        return (
          <span
            className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${
              assetStatusStyles[status]
            }`}
          >
            {status}
          </span>
        );
      },
    },
    {
      header: 'Created Date',
      accessorKey: 'createdAt',
    },
    {
      header: 'Details',
      cell: ({ row }) => (
        <div
          onClick={() => navigate(`/asset-detail/${row.original.id}`)}
          className="w-full flex items-center justify-center cursor-pointer"
        >
          <Eye className="h-4 w-4" />
        </div>
      ),
    },
    ...(assetStatus !== AssetStatus.Draft ? [...availableAssets] : [draftAssets]),
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => <UserActions asset={row.original} />,
      enableSorting: false,
      enableHiding: false,
    },
  ];

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPageIndex(0);
  };

  if (isLoading) {
    return <div className="p-6">Loading assets…</div>;
  }

  if (isError) {
    return <div className="p-6 text-red-500">Failed to load assets</div>;
  }

  const handleFractionalize = async (payload: any) => {
    console.log('Fractionalize Asset ID:', selectedAssetId);
    console.log('Payload:', payload);

    await fractionalizeAsset({
      id: selectedAssetId,
      totalFractions: payload?.totalFractions,
      pricePerFraction: payload?.pricePerFraction,
    }).unwrap();

    setFractionModalOpen(false);
  };

  return (
    <>
      <div className="space-y-8">
        <h2 className="text-3xl font-bold tracking-tight">Asset List</h2>
        <div className="flex items-center gap-5 flex-wrap ">
          <div className="space-y-2">
            <Label>RWA Status</Label>
            <Select value={rwaStatus} onValueChange={(v) => setRwaStatus(v as RwaStatus)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by RWA Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Submitted">Submitted</SelectItem>
                <SelectItem value="Approved">Approved</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Asset Status</Label>
            <Select value={assetStatus} onValueChange={(v) => setAssetStatus(v as AssetStatus)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by Asset Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Draft">Draft</SelectItem>
                <SelectItem value="Available">Available</SelectItem>
                <SelectItem value="Funded">Funded</SelectItem>
                <SelectItem value="Exited">Exited</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Asset Type</Label>
            <Select value={assetType} onValueChange={(v) => setAssetType(v as AssetType)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All</SelectItem>
                <SelectItem value="RealEstate">Real Estate</SelectItem>
                <SelectItem value="Solar">Solar</SelectItem>
                <SelectItem value="Machinery">Machinery</SelectItem>
                <SelectItem value="EVFleet">EV Fleet</SelectItem>
                <SelectItem value="Warehouse">Warehouse</SelectItem>
                <SelectItem value="Store">Store</SelectItem>
                <SelectItem value="Gold">Gold</SelectItem>
                <SelectItem value="Business">Business</SelectItem>
                <SelectItem value="IP">IP</SelectItem>
                <SelectItem value="Others">Others</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Select Date Range</Label>
            <DateRangePicker
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
                // setValue('preferences.dateRange', range);
              }}
              placeholder="Select date range "
            />
          </div>
        </div>

        <DataTable
          data={assets}
          columns={columns}
          pageSize={pageSize}
          pageIndex={pageIndex}
          pageCount={Math.ceil(totalCount / pageSize)}
          manualPagination
          enableFilters={false}
          onSearchChange={handleSearchChange}
          onPaginationChange={(updater) => {
            const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;

            setPageIndex(next.pageIndex);
            setPageSize(next.pageSize);
          }}
        />
      </div>

      <FractionalizeAssetModal
        open={fractionModalOpen}
        onClose={() => {
          setFractionModalOpen(false);
          setSelectedAssetId(null);
        }}
        onConfirm={handleFractionalize}
      />
    </>
  );
}
